import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  Switch,
  FormControlLabel,
  Collapse,
  Checkbox,
} from '@mui/material';
import VerificationBadge from '../components/VerificationBadge';
import { useAuth } from '../hooks/useAuth';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Mail as MailIcon,
  Send as SendIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  ContactPage as IdCardIcon,
} from '@mui/icons-material';
import { Contact, UserSearchResult, UserDeliverySettings } from '../types';
import { getEmailDomainSync } from '../services/systemConfigService';
import api from '../services/api';
import identityDisclosureService, { type IdentityDisclosureShieldSnapshot } from '../services/identityDisclosureService';
import { formatMailingAddressForDisplay } from '../utils/mailingAddressFormat';

const AddressBook: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const normalizeSearchValue = (value?: string): string =>
    (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const formatDate = (value?: string): string | null => {
    if (!value) return null;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString();
  };

  const getIdVerifiedChipTooltip = (user: UserSearchResult): string => {
    const firstDate = formatDate(user.firstIdVerifiedDate);
    const latestDate = formatDate(user.latestIdVerifiedDate);
    const joinedDate = formatDate(user.joinedQixitDate);
    return [
      'Didit government-issued photo ID verification',
      latestDate ? `Most recent verification: ${latestDate}` : null,
      firstDate && firstDate !== latestDate ? `First verification: ${firstDate}` : null,
      joinedDate ? `Joined Qixit: ${joinedDate}` : null
    ].filter(Boolean).join('\n');
  };

  const isIdVerifiedNameChip = (user: UserSearchResult): boolean =>
    Boolean(user.isIdVerified && user.isDisplayNameIdVerifiedName);

  const isTraceableChip = (user: UserSearchResult): boolean =>
    Boolean(user.isIdVerified && !user.isDisplayNameIdVerifiedName);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [fraudDialogOpen, setFraudDialogOpen] = useState(false);
  const [fraudTarget, setFraudTarget] = useState<Contact | null>(null);
  const [fraudSubmitting, setFraudSubmitting] = useState(false);
  const [fraudSelfConfirm, setFraudSelfConfirm] = useState(false);
  const [autoApproveAllRequests, setAutoApproveAllRequests] = useState(true);
  const [autoApproveSaving, setAutoApproveSaving] = useState(false);
  const [shieldDialogOpen, setShieldDialogOpen] = useState(false);
  const [shieldLoading, setShieldLoading] = useState(false);
  const [shieldError, setShieldError] = useState<string | null>(null);
  const [shieldTarget, setShieldTarget] = useState<Contact | null>(null);
  const [shieldSnapshot, setShieldSnapshot] = useState<IdentityDisclosureShieldSnapshot | null>(null);
  const [shieldRequestMailingAddress, setShieldRequestMailingAddress] = useState(false);
  const [approvedShieldByUsername, setApprovedShieldByUsername] = useState<Record<string, boolean>>({});
  const [fraudForm, setFraudForm] = useState({
    requestIdentityPhotos: true,
    requestMailingAddress: false,
  });

  // Default delivery settings (expanded to match legacy system)
  const [deliverySettings, setDeliverySettings] = useState<UserDeliverySettings>({
    defaultDeliveryFeeRequired: 0,
    defaultMaxPayAmount: 0,
    defaultDaysUntilMailExpires: 90,
    maxDailyLimit: 1000,
    isUnlisted: false,
    defaultCommercialDeliveryFee: 0,
    doNotForwardSMTP: false,
    sendNewClientRequiredMessage: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsEdited, setSettingsEdited] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [contactLookupByAddress, setContactLookupByAddress] = useState<Record<string, UserSearchResult>>({});
  const [postageRequiredByContact, setPostageRequiredByContact] = useState<Record<string, number>>({});
  const searchRequestSeqRef = useRef(0);

  // Form state for add from search
  const [addContactForm, setAddContactForm] = useState({
    displayName: '',
    deliveryFeeRequired: '',
    maxPayAmount: '',
  });

  // Form state for manual add/edit
  const [formData, setFormData] = useState({
    displayName: '',
    firstName: '',
    middleName: '',
    lastName: '',
    address: '',
    deliveryRequired: '0',
    maxPay: '0',
  });

  useEffect(() => {
    loadContacts();
    loadDeliverySettings();
    loadIdentityDisclosurePreferences();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Contact[]>('/api/contacts');
      setContacts(data);
      const usernames = Array.from(new Set(
        data
          .map((c) => c.address?.split('@')[0]?.trim())
          .filter((u): u is string => Boolean(u))
      ));

      const enrichedEntries = await Promise.all(
        usernames.map(async (username) => {
          const lower = username.toLowerCase();
          try {
            const [info, postageResp] = await Promise.all([
              api.get<UserSearchResult>(`/api/contacts/lookup/${encodeURIComponent(username)}`),
              api.get<{ requiredPostage: number }>(`/api/contacts/required-postage/${encodeURIComponent(username)}`),
            ]);
            return {
              lower,
              info,
              requiredPostage: postageResp.requiredPostage,
            };
          } catch {
            return { lower, info: null, requiredPostage: null };
          }
        })
      );

      const lookupMap: Record<string, UserSearchResult> = {};
      const postageMap: Record<string, number> = {};
      enrichedEntries.forEach((entry) => {
        if (entry.info) {
          lookupMap[entry.lower] = entry.info;
        }
        if (entry.requiredPostage != null) {
          postageMap[entry.lower] = entry.requiredPostage;
        } else if (entry.info) {
          postageMap[entry.lower] = entry.info.deliveryFeeRequired;
        }
      });
      setContactLookupByAddress(lookupMap);
      setPostageRequiredByContact(postageMap);

      void preloadShieldAvailability(usernames);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const preloadShieldAvailability = async (usernames: string[]) => {
    if (!usernames.length) {
      setApprovedShieldByUsername({});
      return;
    }

    const results = await Promise.all(
      usernames.map(async (username) => {
        try {
          await identityDisclosureService.getShieldSnapshot(username);
          return [username.toLowerCase(), true] as const;
        } catch (err: any) {
          if (err?.response?.status === 404) {
            return [username.toLowerCase(), false] as const;
          }
          return [username.toLowerCase(), false] as const;
        }
      })
    );

    const next: Record<string, boolean> = {};
    results.forEach(([username, hasApprovedSnapshot]) => {
      next[username] = hasApprovedSnapshot;
    });
    setApprovedShieldByUsername(next);
  };

  const loadDeliverySettings = async () => {
    try {
      const data = await api.get<UserDeliverySettings>('/api/contacts/settings');
      setDeliverySettings(data);
    } catch (err: any) {
      console.error('Failed to load delivery settings:', err);
    }
  };

  const loadIdentityDisclosurePreferences = async () => {
    try {
      const pref = await identityDisclosureService.getPreferences();
      setAutoApproveAllRequests(Boolean(pref.autoApproveAllRequests));
    } catch (err) {
      setAutoApproveAllRequests(true);
      console.error('Failed to load identity disclosure preferences:', err);
    }
  };

  const handleAutoApproveToggle = async (enabled: boolean) => {
    const previous = autoApproveAllRequests;
    setAutoApproveAllRequests(enabled);
    setAutoApproveSaving(true);
    try {
      const updated = await identityDisclosureService.updatePreferences({ autoApproveAllRequests: enabled });
      setAutoApproveAllRequests(Boolean(updated.autoApproveAllRequests));
      setSnackbar({
        open: true,
        message: enabled
          ? 'Auto-approve enabled: future ID verification requests auto-approve.'
          : 'Auto-approve disabled: each ID verification request now requires approval.',
        severity: 'success'
      });
    } catch (err: any) {
      setAutoApproveAllRequests(previous);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to update ID disclosure auto-approve setting',
        severity: 'error'
      });
    } finally {
      setAutoApproveSaving(false);
    }
  };

  const saveDeliverySettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await api.put<UserDeliverySettings>('/api/contacts/settings', {
        defaultDeliveryFeeRequired: deliverySettings.defaultDeliveryFeeRequired,
        defaultMaxPayAmount: deliverySettings.defaultMaxPayAmount,
        defaultDaysUntilMailExpires: deliverySettings.defaultDaysUntilMailExpires,
        maxDailyLimit: deliverySettings.maxDailyLimit,
        isUnlisted: deliverySettings.isUnlisted,
        doNotForwardSMTP: deliverySettings.doNotForwardSMTP,
        sendNewClientRequiredMessage: deliverySettings.sendNewClientRequiredMessage,
      });
      setDeliverySettings(data);
      setSettingsEdited(false);
      setSnackbar({ open: true, message: 'Delivery settings saved', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSearchUsers = useCallback(async () => {
    const term = searchTerm.trim();
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }

    const requestSeq = ++searchRequestSeqRef.current;
    setSearchLoading(true);
    try {
      const data = await api.get<UserSearchResult[]>('/api/contacts/search', {
        params: { q: term }
      });
      const normalizedTerm = normalizeSearchValue(term);
      const filtered = data.filter((user) => {
        const usernameNormalized = normalizeSearchValue(user.username);
        const displayNormalized = normalizeSearchValue(user.fullName);
        return usernameNormalized.includes(normalizedTerm) || displayNormalized.includes(normalizedTerm);
      });
      if (requestSeq === searchRequestSeqRef.current) {
        setSearchResults(filtered);
      }
    } catch (err: any) {
      console.error('Search failed:', err);
      if (requestSeq === searchRequestSeqRef.current) {
        setSearchResults([]);
      }
    } finally {
      if (requestSeq === searchRequestSeqRef.current) {
        setSearchLoading(false);
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        handleSearchUsers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, handleSearchUsers]);

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setAddContactForm({
      displayName: (user.fullName || user.username).trim(),
      deliveryFeeRequired: deliverySettings.defaultDeliveryFeeRequired.toString(),
      maxPayAmount: deliverySettings.defaultMaxPayAmount.toString(),
    });
  };

  const handleAddFromSearch = async () => {
    if (!selectedUser) return;
    const canonicalDisplayName = (selectedUser.fullName || selectedUser.username).trim();

    try {
      await api.post('/api/contacts/add-from-search', {
        username: selectedUser.username,
        displayName: canonicalDisplayName || selectedUser.username,
        deliveryFeeRequired: parseFloat(addContactForm.deliveryFeeRequired) || deliverySettings.defaultDeliveryFeeRequired,
        maxPayAmount: parseFloat(addContactForm.maxPayAmount) || deliverySettings.defaultMaxPayAmount,
      });
      setSnackbar({ open: true, message: 'Contact added successfully', severity: 'success' });
      setSearchDialogOpen(false);
      setSelectedUser(null);
      setSearchTerm('');
      setSearchResults([]);
      loadContacts();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to add contact', severity: 'error' });
    }
  };

  const handleOpenDialog = (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        displayName: contact.displayName,
        firstName: contact.firstName || '',
        middleName: contact.middleName || '',
        lastName: contact.lastName || '',
        address: contact.address?.split('@')[0]?.trim() || contact.address,
        deliveryRequired: contact.deliveryRequired.toString(),
        maxPay: contact.maxPay.toString(),
      });
    } else {
      setEditingContact(null);
      setFormData({
        displayName: '',
        firstName: '',
        middleName: '',
        lastName: '',
        address: '',
        deliveryRequired: deliverySettings.defaultDeliveryFeeRequired.toString(),
        maxPay: deliverySettings.defaultMaxPayAmount.toString(),
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const persistedDisplayName = editingContact
      ? (editingContact.displayName || '').trim()
      : formData.displayName.trim();
    if (!persistedDisplayName) {
      setSnackbar({ open: true, message: 'Display name is required', severity: 'error' });
      return;
    }
    const qixitId = formData.address.trim();
    if (!qixitId) {
      setSnackbar({ open: true, message: 'Qixit ID is required', severity: 'error' });
      return;
    }

    const normalizedAddress = qixitId.includes('@')
      ? qixitId
      : `${qixitId}@${getEmailDomainSync()}`;

    const contactData = {
      displayName: persistedDisplayName,
      firstName: formData.firstName.trim() || null,
      middleName: formData.middleName.trim() || null,
      lastName: formData.lastName.trim() || null,
      address: normalizedAddress,
      deliveryRequired: parseFloat(formData.deliveryRequired) || 0,
      maxPay: parseFloat(formData.maxPay) || 0,
    };

    try {
      if (editingContact) {
        await api.put(`/api/contacts/${editingContact.contactID}`, contactData);
        setSnackbar({ open: true, message: 'Contact updated successfully', severity: 'success' });
      } else {
        await api.post('/api/contacts', contactData);
        setSnackbar({ open: true, message: 'Contact created successfully', severity: 'success' });
      }
      handleCloseDialog();
      loadContacts();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to save contact', severity: 'error' });
    }
  };

  const handleDelete = async (contactId: number) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await api.delete(`/api/contacts/${contactId}`);
        setSnackbar({ open: true, message: 'Contact deleted successfully', severity: 'success' });
        loadContacts();
      } catch (err: any) {
        setSnackbar({ open: true, message: err.message || 'Failed to delete contact', severity: 'error' });
      }
    }
  };

  const handleOpenFraudDialog = (
    contact: Contact,
    preset?: Partial<{ requestIdentityPhotos: boolean; requestMailingAddress: boolean }>
  ) => {
    setFraudTarget(contact);
    setFraudSelfConfirm(false);
    setFraudForm({
      requestIdentityPhotos: preset?.requestIdentityPhotos ?? true,
      requestMailingAddress: preset?.requestMailingAddress ?? false,
    });
    setFraudDialogOpen(true);
  };

  const handleShieldClick = async (contact: Contact) => {
    const recipientUsername = getContactUsername(contact);
    if (!recipientUsername) {
      setSnackbar({ open: true, message: 'Could not determine contact username', severity: 'error' });
      return;
    }

    const contactInfo = contactLookupByAddress[recipientUsername.toLowerCase()];
    const isTraceableOrIdVerifiedName = Boolean(contactInfo) &&
      (isTraceableChip(contactInfo) || isIdVerifiedNameChip(contactInfo));
    const hasApprovedSnapshot = Boolean(approvedShieldByUsername[recipientUsername.toLowerCase()]);

    // Green shield on an ID-verified contact with no prior approved disclosure:
    // open the paid request flow instead of calling the snapshot API (which only
    // returns data after the recipient has approved a prior request).
    if (isTraceableOrIdVerifiedName && !hasApprovedSnapshot) {
      handleOpenFraudDialog(contact);
      return;
    }

    // Gray shield flow: allow paid request even when recipient is not yet ID-verified.
    if (!isTraceableOrIdVerifiedName && !hasApprovedSnapshot) {
      handleOpenFraudDialog(contact);
      return;
    }

    setShieldTarget(contact);
    setShieldSnapshot(null);
    setShieldError(null);
    setShieldRequestMailingAddress(false);
    setShieldLoading(true);
    setShieldDialogOpen(true);

    try {
      const snapshot = await identityDisclosureService.getShieldSnapshot(recipientUsername);
      setShieldSnapshot(snapshot);
      setApprovedShieldByUsername((prev) => ({ ...prev, [recipientUsername.toLowerCase()]: true }));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setApprovedShieldByUsername((prev) => ({ ...prev, [recipientUsername.toLowerCase()]: false }));
        setShieldDialogOpen(false);
        handleOpenFraudDialog(contact);
        return;
      }
      setShieldError(err?.response?.data?.message || 'Failed to load approved verification snapshot');
    } finally {
      setShieldLoading(false);
    }
  };

  const getContactUsername = (contact?: Contact | null): string =>
    contact?.address?.split('@')[0]?.trim() || '';

  const getPostageRequiredByContact = (contact: Contact): number | null => {
    const username = getContactUsername(contact).toLowerCase();
    if (!username) return null;
    if (Object.prototype.hasOwnProperty.call(postageRequiredByContact, username)) {
      return postageRequiredByContact[username];
    }
    const contactInfo = contactLookupByAddress[username];
    return contactInfo ? contactInfo.deliveryFeeRequired : null;
  };

  const isSelfFraudTarget = (() => {
    const targetUsername = getContactUsername(fraudTarget).toLowerCase();
    const currentUsername = (user?.username || '').trim().toLowerCase();
    return !!targetUsername && !!currentUsername && targetUsername === currentUsername;
  })();

  const resolveDisplayNameForContact = (contact: Contact): string => {
    const contactUsername = contact.address?.split('@')[0]?.trim().toLowerCase() || '';
    const contactInfo = contactLookupByAddress[contactUsername];
    const canonicalDisplayName = (contactInfo?.fullName || '').trim();
    const storedDisplayName = (contact.displayName || '').trim();
    const shouldUseCanonicalDisplay =
      canonicalDisplayName.length > 0 &&
      storedDisplayName.length > 0 &&
      storedDisplayName.toLowerCase() === contactUsername &&
      canonicalDisplayName.toLowerCase() !== storedDisplayName.toLowerCase();

    return shouldUseCanonicalDisplay ? canonicalDisplayName : contact.displayName;
  };

  const handleComposeToContact = (contact: Contact) => {
    const params = new URLSearchParams();
    params.set('compose', '1');
    params.set('to', contact.address);
    params.set('name', resolveDisplayNameForContact(contact) || contact.address);
    params.set('maxPay', contact.maxPay.toString());
    params.set('required', contact.deliveryRequired.toString());
    navigate(`/messages?${params.toString()}`);
  };

  const handleSubmitFraudRequest = async () => {
    if (!fraudTarget) return;
    const recipientUsername = fraudTarget.address?.split('@')[0]?.trim();
    if (!recipientUsername) {
      setSnackbar({ open: true, message: 'Could not determine contact username', severity: 'error' });
      return;
    }

    if (!fraudForm.requestIdentityPhotos && !fraudForm.requestMailingAddress) {
      setSnackbar({ open: true, message: 'Select at least one verification item', severity: 'error' });
      return;
    }

    setFraudSubmitting(true);
    try {
      const response = await identityDisclosureService.createRequest({
        recipientUsername,
        requestIdentityPhotos: fraudForm.requestIdentityPhotos,
        requestMailingAddress: fraudForm.requestMailingAddress,
      });
      if ((response.status || '').toUpperCase() === 'APPROVED') {
        setApprovedShieldByUsername((prev) => ({ ...prev, [recipientUsername.toLowerCase()]: true }));
      } else {
        setApprovedShieldByUsername((prev) => ({ ...prev, [recipientUsername.toLowerCase()]: false }));
      }
      setFraudDialogOpen(false);
      setFraudTarget(null);
      setSnackbar({
        open: true,
        message: isSelfFraudTarget
          ? 'Self-request submitted as preview. $2.00 non-refundable process fee charged.'
          : 'ID verification request submitted. $2.00 process fee charged.',
        severity: 'success'
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || 'Failed to submit ID verification request',
        severity: 'error'
      });
    } finally {
      setFraudSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Address Book</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={() => setSearchDialogOpen(true)}
          >
            Find Contact
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Manually
          </Button>
        </Box>
      </Box>

      {/* Default Delivery Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">Default Delivery Settings</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            These defaults are applied when adding new contacts. They can be overridden per contact.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ flex: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Default Postage Required from Unknown Senders"
                type="number"
                value={deliverySettings.defaultDeliveryFeeRequired}
                onChange={(e) => {
                  setDeliverySettings(prev => ({ 
                    ...prev, 
                    defaultDeliveryFeeRequired: parseFloat(e.target.value) || 0 
                  }));
                  setSettingsEdited(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Fee required to receive messages from unknown senders"
              />
            </Box>
            <Box sx={{ flex: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Alert Me If Postage Is Over"
                type="number"
                value={deliverySettings.defaultMaxPayAmount}
                onChange={(e) => {
                  setDeliverySettings(prev => ({ 
                    ...prev, 
                    defaultMaxPayAmount: parseFloat(e.target.value) || 0 
                  }));
                  setSettingsEdited(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Warn before send when recipient postage exceeds this amount"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={settingsLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={saveDeliverySettings}
                disabled={!settingsEdited || settingsLoading}
              >
                Save
              </Button>
            </Box>
          </Stack>

          {/* Advanced Settings Expandable Section */}
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
              endIcon={showAdvancedSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none' }}
            >
              {showAdvancedSettings ? 'Hide' : 'Show'} Advanced Settings
            </Button>
            <Collapse in={showAdvancedSettings}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Default Days Until Mail Expires"
                      type="number"
                      value={deliverySettings.defaultDaysUntilMailExpires}
                      onChange={(e) => {
                        setDeliverySettings(prev => ({ 
                          ...prev, 
                          defaultDaysUntilMailExpires: parseInt(e.target.value) || 90 
                        }));
                        setSettingsEdited(true);
                      }}
                      inputProps={{ min: 1, max: 365 }}
                      helperText="Number of days until unread messages expire"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Maximum Daily Transaction Limit"
                      type="number"
                      value={deliverySettings.maxDailyLimit}
                      onChange={(e) => {
                        setDeliverySettings(prev => ({ 
                          ...prev, 
                          maxDailyLimit: parseFloat(e.target.value) || 0 
                        }));
                        setSettingsEdited(true);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 10 }}
                      helperText="Maximum daily transaction limit"
                    />
                  </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.isUnlisted}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            isUnlisted: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Hide from User Search</Typography>
                        <Typography variant="caption" color="text.secondary">
                          When enabled, your account won't appear in search results
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.doNotForwardSMTP}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            doNotForwardSMTP: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Do Not Forward via SMTP</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Disable forwarding messages to external email
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.sendNewClientRequiredMessage}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            sendNewClientRequiredMessage: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Send New Client Required Message</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notify SMTP senders to use Qixit client
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoApproveAllRequests}
                        disabled={autoApproveSaving}
                        onChange={(e) => void handleAutoApproveToggle(e.target.checked)}
                      />
                    }
                    label={(
                      <Box>
                        <Typography variant="body2">Auto-Approve ID Verification Requests</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatically approve future paid identity-photo disclosure requests only. Mailing-address requests always require manual approval.
                        </Typography>
                      </Box>
                    )}
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                </Stack>
              </Box>
            </Collapse>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Contacts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Display Name</TableCell>
                <TableCell>Qixit ID</TableCell>
                <TableCell align="right">Postage Required By Contact</TableCell>
                <TableCell align="right">Postage Required From Contact</TableCell>
                <TableCell align="right">Postage Alert Threshold</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No contacts yet. Use "Find Contact" to search for Qixit users to add!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow
                    key={contact.contactID}
                    hover
                    onDoubleClick={() => handleComposeToContact(contact)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        {(() => {
                          const contactUsername = contact.address?.split('@')[0]?.trim().toLowerCase() || '';
                          const contactInfo = contactLookupByAddress[contactUsername];
                          const canonicalDisplayName = (contactInfo?.fullName || '').trim();
                          const storedDisplayName = (contact.displayName || '').trim();
                          const shouldUseCanonicalDisplay =
                            canonicalDisplayName.length > 0 &&
                            storedDisplayName.length > 0 &&
                            storedDisplayName.toLowerCase() === contactUsername &&
                            canonicalDisplayName.toLowerCase() !== storedDisplayName.toLowerCase();
                          const displayNameToShow = shouldUseCanonicalDisplay ? canonicalDisplayName : contact.displayName;

                          return (
                            <>
                        <Typography variant="body2" fontWeight="bold">
                              {displayNameToShow}
                        </Typography>
                              {(() => {
                                if (!contactInfo) return null;
                                return (
                                  <>
                              {isTraceableChip(contactInfo) && (
                                <Chip label="Traceable" size="small" color="success" variant="outlined" />
                              )}
                              {isIdVerifiedNameChip(contactInfo) && (
                                <Tooltip title={getIdVerifiedChipTooltip(contactInfo)} arrow>
                                  <Chip
                                    label="ID Verified Name"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    icon={<IdCardIcon sx={{ color: 'success.main' }} />}
                                  />
                                </Tooltip>
                              )}
                              <VerificationBadge
                                isEmailVerified={contactInfo.isEmailVerified}
                                isPhoneVerified={contactInfo.isPhoneVerified}
                                isIdVerified={contactInfo.isIdVerified}
                                isAddressVerified={contactInfo.isAddressVerified}
                                verificationLevel={contactInfo.verificationLevel}
                                firstIdVerifiedDate={contactInfo.firstIdVerifiedDate}
                                latestIdVerifiedDate={contactInfo.latestIdVerifiedDate}
                                joinedQixitDate={contactInfo.joinedQixitDate}
                                size="small"
                              />
                                  </>
                                );
                              })()}
                        {(contact.firstName || contact.lastName) && (
                          <Typography variant="caption" color="text.secondary">
                            {[contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(' ')}
                          </Typography>
                        )}
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<MailIcon />}
                        label={getContactUsername(contact) || '—'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Postage you must pay to this contact">
                        {(() => {
                          const requiredPostage = getPostageRequiredByContact(contact);
                          if (requiredPostage == null) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            );
                          }
                          return (
                            <Chip
                              label={`$${requiredPostage.toFixed(2)}`}
                              size="small"
                              color={requiredPostage > 0 ? 'warning' : 'default'}
                            />
                          );
                        })()}
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Postage you require for messages from this contact">
                        <Chip
                          label={`$${contact.deliveryRequired.toFixed(2)}`}
                          size="small"
                          color={contact.deliveryRequired > 0 ? "info" : "default"}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Warn before sending if recipient requires more than this amount">
                        <Chip
                          label={`$${contact.maxPay.toFixed(2)}`}
                          size="small"
                          color={contact.maxPay > 0 ? "success" : "default"}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleComposeToContact(contact)}
                        color="success"
                        title="Compose message"
                      >
                        <SendIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDialog(contact)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => void handleShieldClick(contact)}
                        color={(() => {
                          const contactUsername = getContactUsername(contact).toLowerCase();
                          if (approvedShieldByUsername[contactUsername]) {
                            return 'success';
                          }
                          const contactInfo = contactLookupByAddress[contactUsername];
                          const isTraceableOrIdVerifiedName = Boolean(contactInfo) &&
                            (isTraceableChip(contactInfo) || isIdVerifiedNameChip(contactInfo));
                          return isTraceableOrIdVerifiedName ? 'primary' : 'default';
                        })()}
                        title={(() => {
                          const contactUsername = getContactUsername(contact).toLowerCase();
                          if (approvedShieldByUsername[contactUsername]) {
                            return 'Approved purchased snapshot available';
                          }
                          const contactInfo = contactLookupByAddress[contactUsername];
                          const isTraceableOrIdVerifiedName = Boolean(contactInfo) &&
                            (isTraceableChip(contactInfo) || isIdVerifiedNameChip(contactInfo));
                          return isTraceableOrIdVerifiedName
                            ? 'Traceable / ID-verified contact: pay to request verification details'
                            : 'Untraceable contact: pay to send ID verification request';
                        })()}
                      >
                        <SecurityIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(contact.contactID)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Search Dialog */}
      <Dialog 
        open={searchDialogOpen} 
        onClose={() => {
          setSearchDialogOpen(false);
          setSelectedUser(null);
          setSearchTerm('');
          setSearchResults([]);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAddIcon color="primary" />
            Find and Add Contact
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Search for Qixit users by username or name. The contact's required postage is shown to help you set your payment preferences.
          </Typography>
          
          <TextField
            fullWidth
            placeholder="Search by username or name (minimum 2 characters)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              endAdornment: searchLoading ? <CircularProgress size={20} /> : null,
            }}
            sx={{ mb: 2 }}
          />

          {searchResults.length > 0 && !selectedUser && (
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
              <List dense>
                {searchResults.map((user) => (
                  <ListItemButton 
                    key={user.username}
                    onClick={() => handleSelectUser(user)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography fontWeight="bold">{(user.fullName || user.username).trim()}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`<${user.username}@${getEmailDomainSync()}>`}
                          </Typography>
                          {user.accountType && user.accountType !== 'User' && (
                            <Chip label={user.accountType} size="small" color="primary" variant="outlined" />
                          )}
                          {isTraceableChip(user) && (
                            <Chip label="Traceable" size="small" color="success" variant="outlined" />
                          )}
                          {isIdVerifiedNameChip(user) && (
                            <Tooltip title={getIdVerifiedChipTooltip(user)} arrow>
                              <Chip
                                label="ID Verified Name"
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={<IdCardIcon sx={{ color: 'success.main' }} />}
                              />
                            </Tooltip>
                          )}
                          <VerificationBadge
                            isEmailVerified={user.isEmailVerified}
                            isPhoneVerified={user.isPhoneVerified}
                            isIdVerified={user.isIdVerified}
                            isAddressVerified={user.isAddressVerified}
                            verificationLevel={user.verificationLevel}
                            firstIdVerifiedDate={user.firstIdVerifiedDate}
                            latestIdVerifiedDate={user.latestIdVerifiedDate}
                            joinedQixitDate={user.joinedQixitDate}
                            size="small"
                          />
                          {!user.isActive && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="This user's required postage">
                        <Chip
                          label={`$${user.deliveryFeeRequired.toFixed(2)} required`}
                          size="small"
                          color="info"
                        />
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}

          {searchTerm.length >= 2 && searchResults.length === 0 && !searchLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No users found matching "{searchTerm}"
            </Alert>
          )}

          {selectedUser && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add {selectedUser.username} to Contacts
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This user requires <strong>${selectedUser.deliveryFeeRequired.toFixed(2)}</strong> postage to receive messages.
                </Alert>
                
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Display Name (how they appear in your address book)"
                    value={addContactForm.displayName}
                    disabled
                    helperText="Display name is defined by the alias owner."
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Postage You Require From This Contact"
                      type="number"
                      value={addContactForm.deliveryFeeRequired}
                      onChange={(e) => setAddContactForm(prev => ({ ...prev, deliveryFeeRequired: e.target.value }))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Fee to receive messages FROM this contact"
                    />
                    <TextField
                      fullWidth
                      label="Postage Alert Threshold for Sending TO This Contact"
                      type="number"
                      value={addContactForm.maxPayAmount}
                      onChange={(e) => setAddContactForm(prev => ({ ...prev, maxPayAmount: e.target.value }))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText={`Current required postage is $${selectedUser.deliveryFeeRequired.toFixed(2)}`}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSearchDialogOpen(false);
            setSelectedUser(null);
            setSearchTerm('');
            setSearchResults([]);
          }}>
            Cancel
          </Button>
          {selectedUser && (
            <>
              <Button onClick={() => setSelectedUser(null)}>
                Back to Search
              </Button>
              <Button 
                variant="contained" 
                startIcon={<PersonAddIcon />}
                onClick={handleAddFromSearch}
              >
                Add Contact
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={shieldDialogOpen} onClose={() => setShieldDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verified Identity Snapshot</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {shieldLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!shieldLoading && shieldError && (
              <Alert severity="error">{shieldError}</Alert>
            )}
            {!shieldLoading && !shieldError && shieldSnapshot && (
              <>
                <Alert severity="info">
                  This is a snapshot on the date your request was approved. Requesting an updated verification may, or may not, provide a newer update.
                </Alert>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={shieldRequestMailingAddress}
                      onChange={(e) => setShieldRequestMailingAddress(e.target.checked)}
                    />
                  )}
                  label="Request ID-confirmed mailing address in your next verification request ($2 system fee)."
                />
                <Typography variant="body2">
                  Recipient: <strong>{shieldTarget?.displayName}</strong> ({shieldTarget?.address})
                </Typography>
                <Typography variant="body2">
                  Approved: <strong>{new Date(shieldSnapshot.approvedAtUtc).toLocaleString()}</strong>
                </Typography>
                {shieldSnapshot.evidence.verifiedFullName && (
                  <Typography variant="body2">
                    Verified name: <strong>{shieldSnapshot.evidence.verifiedFullName}</strong>
                  </Typography>
                )}
                {(shieldSnapshot.evidence.idState || shieldSnapshot.evidence.idCountry) && (
                  <Typography variant="body2">
                    ID location: <strong>{[shieldSnapshot.evidence.idState, shieldSnapshot.evidence.idCountry].filter(Boolean).join(', ')}</strong>
                  </Typography>
                )}
                {shieldSnapshot.requestMailingAddress && shieldSnapshot.evidence.mailingAddressLine1 && (
                  <Typography variant="body2">
                    ID-confirmed mailing address:{' '}
                    <strong>
                      {formatMailingAddressForDisplay(shieldSnapshot.evidence)}
                    </strong>
                  </Typography>
                )}
                {shieldSnapshot.requestIdentityPhotos && shieldSnapshot.evidence.portraitImageUrl && (
                  <Box>
                    <Typography variant="subtitle2">ID portrait</Typography>
                    <Box component="img" src={shieldSnapshot.evidence.portraitImageUrl} alt="ID portrait" sx={{ mt: 1, maxWidth: 280, width: '100%', borderRadius: 1 }} />
                  </Box>
                )}
                {shieldSnapshot.requestIdentityPhotos && shieldSnapshot.evidence.livenessVideoUrl && (
                  <Box>
                    <Typography variant="subtitle2">Didit live capture</Typography>
                    <Box component="video" controls src={shieldSnapshot.evidence.livenessVideoUrl} sx={{ mt: 1, maxWidth: 320, width: '100%', borderRadius: 1 }} />
                  </Box>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShieldDialogOpen(false)}>Close</Button>
          {shieldSnapshot && shieldTarget && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setShieldDialogOpen(false);
                handleOpenFraudDialog(
                  shieldTarget,
                  shieldRequestMailingAddress
                    ? { requestIdentityPhotos: false, requestMailingAddress: true }
                    : undefined
                );
              }}
            >
              Request New Verification ($2)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={fraudDialogOpen} onClose={() => setFraudDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ID Verification Request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              This request costs <strong>$2.00</strong>. The recipient is not required to respond.
            </Alert>
            {isSelfFraudTarget && (
              <Alert severity="info">
                You are requesting verification disclosure on your own account as a paid preview.
                The <strong>$2.00 fee is non-refundable</strong>.
              </Alert>
            )}
            <Typography variant="body2">
              Recipient: <strong>{fraudTarget?.displayName}</strong> ({fraudTarget?.address})
            </Typography>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={fraudForm.requestIdentityPhotos}
                  onChange={(e) => setFraudForm((prev) => ({ ...prev, requestIdentityPhotos: e.target.checked }))}
                />
              )}
              label="Request verified true name + ID portrait + Didit live capture"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={fraudForm.requestMailingAddress}
                  onChange={(e) => setFraudForm((prev) => ({ ...prev, requestMailingAddress: e.target.checked }))}
                />
              )}
              label="Request ID-confirmed mailing address"
            />
            <Typography variant="caption" color="text.secondary">
              The recipient may re-verify their ID first if they prefer a different photo to be shown.
            </Typography>
            {isSelfFraudTarget && (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={fraudSelfConfirm}
                    onChange={(e) => setFraudSelfConfirm(e.target.checked)}
                  />
                )}
                label="I understand this is a self-request preview and the $2.00 fee is non-refundable."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFraudDialogOpen(false)} disabled={fraudSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmitFraudRequest}
            disabled={fraudSubmitting || (isSelfFraudTarget && !fraudSelfConfirm)}
          >
            {fraudSubmitting ? 'Submitting...' : 'Pay $2 and Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Contact Dialog (Manual) */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add Contact Manually'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Display Name"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              required
              disabled={Boolean(editingContact)}
              helperText={editingContact
                ? 'Display name is controlled by the alias owner and cannot be edited here.'
                : 'How this contact appears in your address book'}
            />
            <TextField
              fullWidth
              label="Qixit ID"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              disabled={Boolean(editingContact)}
              placeholder="Beta01"
              helperText={editingContact
                ? 'Qixit ID cannot be changed after the contact is added.'
                : 'Username only (without @domain)'}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                label="Middle Name"
                name="middleName"
                value={formData.middleName}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </Stack>

            <Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Payment Settings
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Postage Required From This Contact"
                name="deliveryRequired"
                type="number"
                value={formData.deliveryRequired}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Fee you require to receive from this contact"
              />
              <TextField
                fullWidth
                label="Postage Alert Threshold"
                name="maxPay"
                type="number"
                value={formData.maxPay}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Warn before sending when postage exceeds this amount"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingContact ? 'Save' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddressBook;
